import {Helmet} from 'react-helmet'
import {ChangeEvent, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from '@/translations/i18n'
import {PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {FiltersBar} from '@/components/ui/filters-bar/FiltersBar'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon, AlertTriangleIcon, CheckCircleIcon, Send01Icon} from '@/components/ui/icon/Icon'
import {Badge} from '@/components/ui/badge/Badge'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {Table} from '@/components/commons/table/Table'
import {TableBodyRow} from '@/components/ui/table-body-row/TableBodyRow'
import {TableBodyCell, TableBodyCellText} from '@/components/ui/table-atoms/TableAtoms'
import {MoreActionsCell} from '@/components/ui/more-actions-cell/MoreActionsCell'
import {TableFilters} from '@/components/commons/table-filters/TableFilters'
import {TableInputSearch} from '@/components/commons/table-input-search/TableInputSearch'
import {DateRangeFilter} from '@/components/commons/date-range-filter/DateRangeFilter'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {PaginationResponse, DateRangeFilterEnum} from '@/types'
import {formatLocaleDate} from '@/dayjs'
import {z} from 'zod'
import {debounce, filtersParamToPayload} from '@/utilities/helpers'
import {useGetSiarExportHistory} from '@/features/projects/siar-bulk-export/queries/useGetSiarExportHistory'
import {HttpGetSiarExportHistoryParams} from '@/features/projects/siar-bulk-export/http/siarBulkExport.http'
import {
    SiarBulkExportType,
    SiarExportHistorySearchParams,
    SiarExportStatus,
    SiarExportsColumn
} from '@/features/projects/siar-bulk-export/types'
import {siarExportColumnToLabel, siarExportTypeToLabel} from '@/features/projects/siar-bulk-export/utils'
import {SiarExportRowMenu} from '@/features/projects/siar-bulk-export/components/siar-export-row-menu/SiarExportRowMenu'
import {BulkSiarExportDialog} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportDialog'
import {SiarExportDetailDialog} from '@/features/projects/siar-bulk-export/components/siar-export-detail-dialog/SiarExportDetailDialog'
import {
    StyledCreatedAtCell,
    StyledExportNameCell,
    StyledExportNameSubtitleRow,
    StyledExportNameTitleRow,
    StyledWrapper
} from './style'

const extractSingleEnumValue = <S extends z.ZodSchema>(
    filtersParam: string | undefined,
    key: string,
    schema: S
): z.infer<S> | undefined => {
    const payload = filtersParam ? filtersParamToPayload(filtersParam) : undefined
    const field = payload?.filters?.fields.find(f => f.key === key)
    if (!field || field.operator !== 'in') return undefined
    const values = Array.isArray(field.value) ? field.value : []
    if (values.length !== 1) return undefined
    const parsed = schema.safeParse(values[0])
    return parsed.success ? parsed.data : undefined
}

const extractFilterDateValue = (filtersParam: string | undefined, key: string): string | undefined => {
    const payload = filtersParam ? filtersParamToPayload(filtersParam) : undefined
    const field = payload?.filters?.fields.find(f => f.key === key)
    if (!field || typeof field.value !== 'string') return undefined
    return field.value
}

export const SiarExportHistory = () => {
    const {t} = useTranslation()
    const inputSearchRef = useRef<HTMLInputElement | null>(null)
    const {searchParams, setSearchParams} = useParsedSearchParams(SiarExportHistorySearchParams)

    const apiParams = useMemo<HttpGetSiarExportHistoryParams>(() => {
        const exportTypeFromFilters = extractSingleEnumValue(searchParams.filters, 'exportType', SiarBulkExportType)
        const statusFromFilters = extractSingleEnumValue(searchParams.filters, 'status', SiarExportStatus)
        const dateFromFromFilters = extractFilterDateValue(searchParams.filters, 'dateFrom')
        const dateToFromFilters = extractFilterDateValue(searchParams.filters, 'dateTo')
        return {
            exportType: exportTypeFromFilters ?? searchParams.exportType,
            dateFrom: searchParams.startDate ?? dateFromFromFilters ?? searchParams.dateFrom,
            dateTo: searchParams.endDate ?? dateToFromFilters ?? searchParams.dateTo,
            status: statusFromFilters,
            projectNumber: searchParams.projectNumber,
            page: searchParams.page,
            limit: searchParams.limit
        }
    }, [searchParams])

    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({projectNumber: event.target.value || undefined, page: 1})
    }, 500)

    const [persistedPagination, setPersistedPagination] = useState<PaginationResponse>({
        total: 0,
        lastPage: 1,
        perPage: searchParams.limit,
        currentPage: searchParams.page
    })

    const {data, isPending, isError} = useGetSiarExportHistory(apiParams)
    const rows = data?.items ?? []
    const [detailExportId, setDetailExportId] = useState<string | null>(null)

    useEffect(() => {
        if (data) setPersistedPagination(data.meta)
    }, [data])

    const handleApplyDateRange = (values: {startDate: string; endDate: string; range: DateRangeFilterEnum}) => {
        setSearchParams({...values, page: 1})
    }

    return (
        <StyledWrapper>
            <Helmet title={t('projects:bulk_siar_export_history_meta_title')} />
            <PageHeading direction="column" gap={4}>
                <Flexbox fullWidth justify="space-between" align="center" gap={2}>
                    <h2>{t('projects:bulk_siar_export_history_title')}</h2>
                    <BulkSiarExportDialog onShowDetail={setDetailExportId} />
                </Flexbox>
                <DateRangeFilter
                    values={
                        searchParams.startDate && searchParams.endDate && searchParams.range
                            ? {
                                  startDate: searchParams.startDate,
                                  endDate: searchParams.endDate,
                                  range: searchParams.range
                              }
                            : undefined
                    }
                    onApply={handleApplyDateRange}
                    onClear={() =>
                        setSearchParams({startDate: undefined, endDate: undefined, range: undefined, page: 1})
                    }
                    visibleOptions={[DateRangeFilterEnum.Enum.month]}
                />
            </PageHeading>
            <FiltersBar>
                <Flexbox fullWidth justify="space-between" align="center" gap={2}>
                    <TableInputSearch
                        ref={inputSearchRef}
                        defaultValue={searchParams.projectNumber}
                        onChange={onSearch}
                        placeholder={t('projects:bulk_siar_export_history_search_placeholder')}
                    />
                    <TableFilters
                        onApply={filters => setSearchParams({filters: JSON.stringify(filters), page: 1})}
                        filtersParams={searchParams.filters}
                        entity="siarExports"
                        availableFilters={[
                            {
                                key: 'exportType',
                                label: siarExportColumnToLabel[SiarExportsColumn.enum.exportType]
                            },
                            {
                                key: 'status',
                                label: siarExportColumnToLabel[SiarExportsColumn.enum.status]
                            },
                            {
                                key: 'dateFrom',
                                label: 'projects:bulk_siar_export_date_from'
                            },
                            {
                                key: 'dateTo',
                                label: 'projects:bulk_siar_export_date_to'
                            }
                        ]}
                    />
                </Flexbox>
            </FiltersBar>
            {isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState
                            title={t('projects:bulk_siar_export_history_empty_title')}
                            subtitle={t('projects:bulk_siar_export_history_empty_subtitle')}
                            style={{border: 'none', height: '100%'}}
                        />
                    }
                    rowComponent={({tableBodyRowProps, dataItem, index}) => (
                        <TableBodyRow
                            key={index}
                            {...tableBodyRowProps}
                            onClick={() => dataItem && setDetailExportId(dataItem.id)}
                        />
                    )}
                    columnPinning={{
                        right: [SiarExportsColumn.enum.moreActions]
                    }}
                    isLoading={isPending}
                    data={rows}
                    pagination={{
                        ...persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => setSearchParams({limit, page: 1})
                    }}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledExportNameCell>
                                        <StyledExportNameSubtitleRow>{dataItem.fileName}</StyledExportNameSubtitleRow>
                                        <StyledExportNameTitleRow>
                                            <span>
                                                {t(siarExportTypeToLabel[dataItem.exportType])}
                                                {' · '}
                                                {t('projects:bulk_siar_export_history_subtitle', {
                                                    count: dataItem.projectCount
                                                })}
                                            </span>
                                        </StyledExportNameTitleRow>
                                    </StyledExportNameCell>
                                </TableBodyCell>
                            ),
                            header: () => t(siarExportColumnToLabel[SiarExportsColumn.enum.name]),
                            id: SiarExportsColumn.enum.name,
                            size: () => 360
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                const from = dataItem.dateFrom ? formatLocaleDate(dataItem.dateFrom, 'll') : null
                                const to = dataItem.dateTo ? formatLocaleDate(dataItem.dateTo, 'll') : null
                                let label: string
                                if (from && to) label = `${from} → ${to}`
                                else if (from) label = `${t('projects:bulk_siar_export_date_from')}: ${from}`
                                else if (to) label = `${t('projects:bulk_siar_export_date_to')}: ${to}`
                                else label = '-'
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>{label}</TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(siarExportColumnToLabel[SiarExportsColumn.enum.period]),
                            id: SiarExportsColumn.enum.period,
                            size: () => 240
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledCreatedAtCell>
                                        <strong>{formatLocaleDate(dataItem.createdAt, 'lll')}</strong>
                                        <span>{`${dataItem.createdBy.firstName} ${dataItem.createdBy.lastName}`}</span>
                                    </StyledCreatedAtCell>
                                </TableBodyCell>
                            ),
                            header: () => t(siarExportColumnToLabel[SiarExportsColumn.enum.createdAtAndAuthor]),
                            id: SiarExportsColumn.enum.createdAtAndAuthor,
                            size: () => 220
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                const pendingTransmissionCount = Math.max(
                                    0,
                                    dataItem.projectCount - dataItem.transmittedCount
                                )
                                const variantByStatus = {
                                    pending: 'orange',
                                    partially_transmitted: 'danger',
                                    transmitted: 'success'
                                } as const
                                const iconByStatus = {
                                    pending: <Send01Icon size={12} />,
                                    partially_transmitted: <AlertCircleIcon size={12} />,
                                    transmitted: <CheckCircleIcon size={12} />
                                }
                                const labelByStatus = {
                                    pending: t('projects:bulk_siar_export_status_to_transmit_progress', {
                                        pending: pendingTransmissionCount,
                                        total: dataItem.projectCount
                                    }),
                                    partially_transmitted: t(
                                        'projects:bulk_siar_export_status_partially_transmitted_progress',
                                        {pending: pendingTransmissionCount, total: dataItem.projectCount}
                                    ),
                                    transmitted: t('projects:bulk_siar_export_status_transmission_completed')
                                }
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <Badge variant={variantByStatus[dataItem.status]} size="sm">
                                            {iconByStatus[dataItem.status]}
                                            {labelByStatus[dataItem.status]}
                                        </Badge>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(siarExportColumnToLabel[SiarExportsColumn.enum.status]),
                            id: SiarExportsColumn.enum.status,
                            size: () => 260
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <MoreActionsCell {...forwardedProps}>
                                    <SiarExportRowMenu
                                        siarExport={dataItem}
                                        onShowDetail={() => setDetailExportId(dataItem.id)}
                                    />
                                </MoreActionsCell>
                            ),
                            header: () => undefined,
                            id: SiarExportsColumn.enum.moreActions,
                            size: () => 56
                        }
                    ]}
                />
            )}
            {detailExportId && (
                <SiarExportDetailDialog
                    exportId={detailExportId}
                    isOpen={!!detailExportId}
                    onClose={() => setDetailExportId(null)}
                />
            )}
        </StyledWrapper>
    )
}
